import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {Subscription} from 'rxjs';
import {filter} from 'rxjs/operators';
import {HelpTourService} from "../../service/api/help-tour.service";
import {HelpTourDto, HelpTourStep} from "../../model/dto/help-tour.dto";

/**
 * Guided tour component using Driver.js.
 * Automatically loads uncompleted tours for the current module/route.
 * Supports auto-show for first-time users and manual triggering.
 *
 * Driver.js must be installed: npm install driver.js
 */
@Component({
    selector: 'mvs-guided-tour',
    template: '', // No template needed - Driver.js creates its own overlay
    standalone: false
})
export class GuidedTourComponent implements OnInit, OnChanges, OnDestroy {

    @Input() moduleAlias: string;

    initialized = false;

    private subscriptions: Subscription[] = [];
    private driverInstance: any;
    private currentTour: HelpTourDto;

    constructor(
        private tourService: HelpTourService,
        private router: Router
    ) {}

    ngOnInit(): void {
        this.initComponent();
    }

    /**
     * Initialize Component.
     */
    initComponent(): void {
        // Check for auto-show tours on route change
        const sub = this.router.events.pipe(
            filter(event => event instanceof NavigationEnd)
        ).subscribe((event: NavigationEnd) => {
            this.checkForAutoShowTours(event.urlAfterRedirects);
        });
        this.subscriptions.push(sub);

        // Check on initial load
        this.checkForAutoShowTours(this.router.url);
        this.initialized = true;
    }

    /**
     * Process changes within Binding.
     * @param changes
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }

        if (changes['moduleAlias']) {
            this.checkForAutoShowTours(this.router.url);
        }
    }

    /**
     * Destroy component.
     */
    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.destroyDriver();
    }

    /**
     * Start a specific tour by ID.
     */
    startTour(tourId: number): void {
        this.tourService.getTour(tourId).subscribe({
            next: tour => this.runTour(tour),
            error: err => console.error('Failed to load tour', err)
        });
    }

    /**
     * Start a tour from a HelpTourDto object.
     */
    startTourFromDto(tour: HelpTourDto): void {
        this.runTour(tour);
    }

    private checkForAutoShowTours(currentUrl: string): void {
        if (!this.moduleAlias) return;

        this.tourService.getUncompletedTours(this.moduleAlias).subscribe({
            next: tours => {
                const autoShowTour = tours.find(t =>
                    t.autoShow && t.triggerRoute && currentUrl.includes(t.triggerRoute)
                );
                if (autoShowTour) {
                    // Small delay to let the page render
                    setTimeout(() => this.runTour(autoShowTour), 500);
                }
            },
            error: err => console.error('Failed to check for auto-show tours', err)
        });
    }

    private async runTour(tour: HelpTourDto): Promise<void> {
        this.currentTour = tour;

        let steps: HelpTourStep[];
        try {
            steps = JSON.parse(tour.stepsJson);
        } catch (e) {
            console.error('Failed to parse tour steps JSON', e);
            return;
        }

        if (!steps || steps.length === 0) return;

        // Dynamically import driver.js (optional dependency - install via: npm install driver.js)
        try {
            // eslint-disable-next-line @typescript-eslint/no-require-imports
            const driverModule: any = await (Function('return import("driver.js")')());
            const {driver} = driverModule;

            this.driverInstance = driver({
                showProgress: true,
                showButtons: ['next', 'previous', 'close'],
                nextBtnText: 'Weiter',
                prevBtnText: 'Zurueck',
                doneBtnText: 'Fertig',
                progressText: '{{current}} von {{total}}',
                steps: steps.map(step => ({
                    element: step.element,
                    popover: {
                        title: step.title,
                        description: step.description,
                        side: (step.position as any) || 'bottom',
                        align: 'start'
                    }
                })),
                onDestroyStarted: () => {
                    this.onTourComplete(steps.length - 1);
                    this.driverInstance.destroy();
                }
            });

            this.driverInstance.drive();
        } catch (e) {
            console.warn('Driver.js not available. Install via: npm install driver.js', e);
        }
    }

    private onTourComplete(lastStepIndex: number): void {
        if (!this.currentTour) return;

        this.tourService.completeTour(this.currentTour.id, lastStepIndex).subscribe({
            next: () => console.log('Tour completed:', this.currentTour.title),
            error: err => console.error('Failed to mark tour as completed', err)
        });
    }

    private destroyDriver(): void {
        if (this.driverInstance) {
            try {
                this.driverInstance.destroy();
            } catch (e) {
                // Ignore cleanup errors
            }
            this.driverInstance = null;
        }
    }
}
